import React from 'react'

import { REVEAL_EVENT } from 'browser/components/atomic-elements/atoms/form-field/form-field-shell/reveal-form-field'
import {
  FormFocusTargetContext,
  IFocusTarget,
  IFormFocusTargetContext,
  IFormFocusHandle,
  FormFocusHandleContext,
} from 'shared-libs/components/ui2/behavior/form-focus-target'
import type { PulseIntent } from 'shared-libs/components/ui2/behavior/pulse-highlight'

interface IFormFocusTargetProviderProps {
  children: React.ReactNode
}

// Web implementation of IFormFocusTargetContext<HTMLElement>. Registry
// state lives on refs so descendant register/unregister calls don't
// re-render the tree.
//
// Timing is owned here — callers `await focusFirstError()` without
// scheduling their own frame:
//   1. Wait one animation frame so any preceding setState (e.g. flipping
//      `hasSubmitted`) has committed and every field's own
//      `hasError()` returns the up-to-date value.
//   2. Order candidates primarily by registration order (declaration
//      order — sections register their fields in mount order and each
//      field's `sectionId` is used as a secondary key when we need it),
//      then by `getBoundingClientRect().top` within the same section.
//      This keeps cross-section walks deterministic even when inactive
//      sections have zero-sized bounds under `display: none`.
//   3. Ask the target to make itself visible (`revealSection`) — fields
//      inside a wizard jump the wizard from there, since the provider
//      itself mounts above `UI2Form` and has no view of the wizard
//      context. Then wait one more frame for the wizard's section swap
//      to commit, dispatch REVEAL_EVENT to expand any collapsed
//      containers, and scroll on the fresh layout.
const FormFocusTargetContextProvider = FormFocusTargetContext.Provider as React.ComponentType<{
  value: IFormFocusTargetContext
  children?: React.ReactNode
}>

// Wait one animation frame — resolves after the browser's next paint, i.e.
// after any pending React commit and layout pass.
function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()))
}

export const FormFocusTargetProvider: React.FC<IFormFocusTargetProviderProps> = ({ children }) => {
  const targetsRef = React.useRef<Map<string, IFocusTarget<HTMLElement>>>(new Map())

  const contextValue = React.useMemo<IFormFocusTargetContext<HTMLElement>>(() => {
    const completeFocus = async (
      path: string,
      intent: PulseIntent = 'focus',
    ): Promise<boolean> => {
      const target = targetsRef.current.get(path)
      if (!target) return false
      const node = target.getRef()
      if (!node) return false
      // Ask any collapsible ancestor (FormSection, Section, UI2FormSection)
      // to expand itself before we measure the scroll target. Listeners flip
      // their own state synchronously; we then wait one frame so React
      // paints the expanded layout before scrollIntoView reads offsets.
      node.dispatchEvent(new CustomEvent(REVEAL_EVENT, { bubbles: true }))
      await nextFrame()
      node.scrollIntoView({ behavior: 'smooth', block: 'center' })
      if (typeof (node as HTMLElement).focus === 'function' && isFocusable(node)) {
        (node as HTMLElement).focus({ preventScroll: true })
      }
      // A field that is actually in error pulses red even when the caller
      // asked for a plain focus — the ring shouldn't contradict the error
      // message sitting right under it.
      target.pulse?.(target.hasError?.() ? 'error' : intent)
      return true
    }

    // Assign each distinct sectionId a rank based on the order in which
    // it first appeared in the target registry. Since UI2FormSection
    // wraps children in UI2FormSectionIdContext.Provider and mounts in
    // declaration order (inactive sections still mount under
    // `display: none`), this rank matches the wizard's declaration
    // order. Fields without a sectionId share bucket 0, so non-wizard
    // forms still sort by pure layout order.
    const sectionRanks = (): Map<string | undefined, number> => {
      const ranks = new Map<string | undefined, number>()
      let next = 0
      targetsRef.current.forEach((target) => {
        const id = target.sectionId
        if (ranks.has(id)) return
        ranks.set(id, id ? ++next : 0)
      })
      return ranks
    }

    // Walk registered targets, throw away ones whose ref isn't in the
    // document, then sort by (section rank, viewport top). Section rank
    // wins so an errored field in an earlier-declared section always
    // beats a later section even when the earlier one is currently
    // hidden by `display: none` (all its descendants report top = 0).
    const pathsInLayoutOrder = (): string[] => {
      const ranks = sectionRanks()
      const rows: Array<{ path: string; section: number; top: number }> = []
      targetsRef.current.forEach((target, path) => {
        const node = target.getRef()
        if (!node || !node.isConnected) return
        rows.push({
          path,
          section: ranks.get(target.sectionId) ?? 0,
          top: node.getBoundingClientRect().top,
        })
      })
      rows.sort((a, b) => (a.section - b.section) || (a.top - b.top))
      return rows.map((r) => r.path)
    }

    // Shared body of focusPath — `focusFirstError` routes through it with
    // an 'error' intent so the ring lands red instead of the default
    // highlight color.
    const focusPathWithIntent = async (path: string, intent: PulseIntent): Promise<boolean> => {
      // Wait one frame so any state update that triggered this call has
      // committed and the target's ref points at the final layout.
      await nextFrame()
      const target = targetsRef.current.get(path)
      if (!target) return false
      // Fields inside a wizard call `UI2WizardContext.jumpTo(sectionId)`
      // here when their section is inactive. We don't reach into the
      // wizard from the provider — this provider sits above UI2Form and
      // wouldn't see the right context anyway.
      target.revealSection?.()
      // Wait for the wizard's section swap (if any) to commit before
      // completeFocus reads a ref, dispatches REVEAL_EVENT, and scrolls.
      await nextFrame()
      return await completeFocus(path, intent)
    }

    const value: IFormFocusTargetContext<HTMLElement> = {
      register: (path, target) => {
        targetsRef.current.delete(path)
        targetsRef.current.set(path, target as IFocusTarget<HTMLElement>)
      },
      unregister: (path) => {
        targetsRef.current.delete(path)
      },
      getRef: (path) => {
        const t = targetsRef.current.get(path)
        return t ? t.getRef() : null
      },
      getOrderedPaths: () => pathsInLayoutOrder(),
      focusPath: (path) => focusPathWithIntent(path, 'focus'),
      focusFirstError: async (scope) => {
        // Same frame gate — `hasError()` reads each field's freshest
        // props (including any newly-merged UI errors from `hasSubmitted`).
        await nextFrame()
        for (const path of pathsInLayoutOrder()) {
          const target = targetsRef.current.get(path)
          if (!target) continue
          if (scope?.sectionId && target.sectionId !== scope.sectionId) continue
          if (target.hasError?.()) {
            // Delegate the jump-and-focus flow (already frame-gated).
            await focusPathWithIntent(path, 'error')
            return path
          }
        }
        return undefined
      },
    }
    return value
  }, [])

  // Publish the imperative handle up to any host that opted in via
  // FormFocusHandleContext. Stable identity via contextValue.
  const handle = React.useMemo<IFormFocusHandle>(() => ({
    focusFirstError: (scope) => contextValue.focusFirstError(scope),
    focusPath: (path) => contextValue.focusPath(path),
    getOrderedPaths: () => contextValue.getOrderedPaths(),
    getTarget: (path) => targetsRef.current.get(path) as IFocusTarget | undefined,
  }), [contextValue])
  const handleSetter = React.useContext(FormFocusHandleContext)
  React.useEffect(() => {
    if (!handleSetter) return
    handleSetter(handle)
    return () => handleSetter(null)
  }, [handleSetter, handle])

  return (
    <FormFocusTargetContextProvider value={contextValue as IFormFocusTargetContext}>
      {children}
    </FormFocusTargetContextProvider>
  )
}

function isFocusable(node: HTMLElement): boolean {
  const tag = node.tagName
  if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'BUTTON') return true
  return node.hasAttribute('tabindex')
}
